// Native Web Audio Synthesis Engine (Zero MP3/External Downloads)
// Provides instant, latency-free tactile feedback for interactions and security events.

class SoundEffectsEngine {
  private ctx: AudioContext | null = null;
  private soundEnabled: boolean = true;

  constructor() {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('dms_sound_enabled');
      this.soundEnabled = stored !== 'false';
    }
  }

  private getAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;

    if (!this.ctx) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.ctx = new AudioContextClass();
      }
    }

    if (this.ctx && this.ctx.state === 'suspended') {
      void this.ctx.resume().catch(() => undefined);
    }

    return this.ctx;
  }

  public isEnabled(): boolean {
    return this.soundEnabled;
  }

  public setEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    if (typeof window !== 'undefined') {
      localStorage.setItem('dms_sound_enabled', enabled ? 'true' : 'false');
    }
    if (enabled) {
      this.playClick();
    }
  }

  public toggle(): boolean {
    const next = !this.soundEnabled;
    this.setEnabled(next);
    return next;
  }

  /**
   * Tactile Click (playClick()):
   * Subtle micro-tick on button taps, slider interaction, password toggle, or reset.
   */
  public playClick(pitchMultiplier: number = 1.0): void {
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    try {
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(1400 * pitchMultiplier, t);
      osc.frequency.exponentialRampToValueAtTime(320 * pitchMultiplier, t + 0.035);

      gain.gain.setValueAtTime(0.08, t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.035);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(t);
      osc.stop(t + 0.04);
    } catch {
      // AudioContext policy catch
    }
  }

  /**
   * Lock Snap (playSnap()):
   * Crisp, satisfying mechanical chime when the vault key locks into its target slot.
   */
  public playSnap(): void {
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    try {
      const t = ctx.currentTime;

      // 1. Primary metallic chime (fundamental + harmonic)
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();

      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(987.77, t); // B5
      osc1.frequency.exponentialRampToValueAtTime(1318.51, t + 0.06); // E6

      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(1975.53, t); // B6 metallic overtone
      osc2.frequency.exponentialRampToValueAtTime(2637.02, t + 0.08); // E7

      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      osc1.start(t);
      osc2.start(t);
      osc1.stop(t + 0.24);
      osc2.stop(t + 0.24);

      // 2. Tactile click transient for mechanical snap feel
      const clickOsc = ctx.createOscillator();
      const clickGain = ctx.createGain();
      clickOsc.type = 'square';
      clickOsc.frequency.setValueAtTime(2400, t);
      clickOsc.frequency.exponentialRampToValueAtTime(200, t + 0.02);

      clickGain.gain.setValueAtTime(0.12, t);
      clickGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.025);

      clickOsc.connect(clickGain);
      clickGain.connect(ctx.destination);

      clickOsc.start(t);
      clickOsc.stop(t + 0.03);
    } catch {
      // AudioContext policy catch
    }
  }

  /**
   * Success Chord (playSuccess()):
   * Soft major harmonic chord (C5 → E5 → G5 → C6) when "Login Successful" triggers.
   */
  public playSuccess(): void {
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    try {
      const t = ctx.currentTime;
      // C5, E5, G5, C6 frequencies
      const notes = [523.25, 659.25, 783.99, 1046.50];

      notes.forEach((freq, idx) => {
        const noteTime = t + idx * 0.08;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, noteTime);

        // Warm subtle vibrato on final resolution note
        if (idx === notes.length - 1) {
          osc.frequency.linearRampToValueAtTime(freq * 1.004, noteTime + 0.45);
        }

        gain.gain.setValueAtTime(0.0001, noteTime);
        gain.gain.linearRampToValueAtTime(0.14, noteTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, noteTime + 0.65);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(noteTime);
        osc.stop(noteTime + 0.7);
      });
    } catch {
      // AudioContext policy catch
    }
  }

  /**
   * Warning Tone (playWarning()):
   * Dual-frequency alert pulse when a validation field or slider authorization is missing.
   */
  public playWarning(): void {
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    try {
      const t = ctx.currentTime;
      const pulses = [0, 0.11];

      pulses.forEach((offset) => {
        const pulseTime = t + offset;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(587.33, pulseTime); // D5
        osc.frequency.exponentialRampToValueAtTime(440.00, pulseTime + 0.07); // A4

        gain.gain.setValueAtTime(0.12, pulseTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, pulseTime + 0.08);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(pulseTime);
        osc.stop(pulseTime + 0.09);
      });
    } catch {
      // AudioContext policy catch
    }
  }

  /**
   * Soft Error Thud (playError()):
   * Low-frequency feedback tone when credentials mismatch or verification failed.
   */
  public playError(): void {
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    try {
      const t = ctx.currentTime;

      // Low bass thud dropping downwards
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(190, t);
      osc.frequency.exponentialRampToValueAtTime(55, t + 0.22);

      gain.gain.setValueAtTime(0.22, t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(t);
      osc.stop(t + 0.26);
    } catch {
      // AudioContext policy catch
    }
  }

  /**
   * Sign Out Chime (playSignOut()):
   * Descending harmonic chime (G5 → E5 → C5 → G4) with a soft closing vault lock tone.
   */
  public playSignOut(): void {
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    try {
      const t = ctx.currentTime;
      // Descending harmonic notes: G5, E5, C5, G4
      const notes = [783.99, 659.25, 523.25, 392.00];

      notes.forEach((freq, idx) => {
        const noteTime = t + idx * 0.085;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, noteTime);

        // Soft exponential release
        gain.gain.setValueAtTime(0.0001, noteTime);
        gain.gain.linearRampToValueAtTime(0.12, noteTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, noteTime + 0.45);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(noteTime);
        osc.stop(noteTime + 0.5);
      });

      // Soft mechanical closure tick at the tail (simulating safe vault lock engagement)
      const lockTime = t + 0.28;
      const lockOsc = ctx.createOscillator();
      const lockGain = ctx.createGain();

      lockOsc.type = 'triangle';
      lockOsc.frequency.setValueAtTime(800, lockTime);
      lockOsc.frequency.exponentialRampToValueAtTime(120, lockTime + 0.04);

      lockGain.gain.setValueAtTime(0.07, lockTime);
      lockGain.gain.exponentialRampToValueAtTime(0.0001, lockTime + 0.045);

      lockOsc.connect(lockGain);
      lockGain.connect(ctx.destination);

      lockOsc.start(lockTime);
      lockOsc.stop(lockTime + 0.05);
    } catch {
      // AudioContext policy catch
    }
  }
}

export const sounds = new SoundEffectsEngine();
